import { Component } from '@theme/component';

/**
 * Oath PDP "How to Style" / "About the Movement" — a fanning scale-carousel.
 *
 * Built on Horizon's Component base + ref system (same convention as marquee.js /
 * oath-story-tabs.js), per the S25 native-conventions doctrine. Figma 824:5673 /
 * 1222:3453. Single source of truth = the active index.
 *
 * Strip DOM = [clone(last), slide0..slideN-1, clone(first)] — the clones give a
 * neighbour peek on both ends so all three images always show (the fan), including
 * on slide 1. Only the real slides carry ref="card[]"; clones are queried directly.
 *
 * S33 (id 54): rebuilt for (1) a CONTINUOUS loop — advancing past the last slide
 * slides FORWARD into clone(first) then silently snaps to slide 0 (and the mirror
 * for prev past slide 0), instead of rewinding the whole strip backward; and (2)
 * click/touch DRAG — drag the strip to scroll, snapping to prev/next/current on
 * release. The wrap scales the target clone during the animation so there is no
 * size-pop at the silent snap. Respects prefers-reduced-motion (instant, no
 * transitionend dependency). Arrows behave exactly as before.
 *
 * Centring is ANALYTIC (computed from the fixed card sizes read off CSS custom
 * properties) — the width transition means getBoundingClientRect mid-animation
 * returns intermediate values, so positions are derived from --hts-card-side /
 * --hts-card-active / --hts-gap. #centerSlot(S) centres DOM slot S, where slot 0 is
 * the left clone, slots 1..N are real slides, slot N+1 is the right clone.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} prev
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} strip
 * @property {HTMLElement[]} card - real slide cards (ref="card[]"), clones excluded
 * @property {HTMLElement[]} text - per-slide text blocks (ref="text[]")
 * @extends Component<Refs>
 */
class OathHowToStyle extends Component {
  requiredRefs = ['prev', 'next', 'strip', 'card', 'text'];

  #active = 0;
  #tx = 0;
  #cloneLeft = null;
  #cloneRight = null;
  #settleHandler = null;
  #settleTimer = 0;
  #drag = null;
  #onResize;

  connectedCallback() {
    super.connectedCallback();
    const { prev, next, strip, card } = this.refs;
    if (!card || card.length === 0) return;

    const clones = this.querySelectorAll('.oath-how-to-style__card--clone');
    this.#cloneLeft = clones[0] || null;
    this.#cloneRight = clones[1] || null;

    prev.addEventListener('click', () => this.#go(-1));
    next.addEventListener('click', () => this.#go(1));

    // Drag-to-scroll (pointer = mouse + touch). Bound to the clipped viewport.
    const viewport = strip.parentElement;
    if (viewport) {
      viewport.addEventListener('pointerdown', this.#onPointerDown);
      viewport.addEventListener('pointermove', this.#onPointerMove);
      viewport.addEventListener('pointerup', this.#onPointerUp);
      viewport.addEventListener('pointercancel', this.#onPointerUp);
    }

    requestAnimationFrame(() => this.#activate(0, false));
    this.#onResize = () => this.#center(false);
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#onResize) window.removeEventListener('resize', this.#onResize);
    this.#clearSettle();
  }

  get #reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Geometry read off CSS custom props (analytic, not measured). */
  #metrics() {
    const cs = getComputedStyle(this);
    return {
      side: parseFloat(cs.getPropertyValue('--hts-card-side')) || 0,
      active: parseFloat(cs.getPropertyValue('--hts-card-active')) || 0,
      gap: parseFloat(cs.getPropertyValue('--hts-gap')) || 0,
    };
  }

  /**
   * Translate the strip so DOM slot `S` is centred. Slot 0 = left clone, slots
   * 1..N = real slides, slot N+1 = right clone. The centred card is active-width.
   * @param {number} slot
   * @param {boolean} animate
   */
  #centerSlot(slot, animate) {
    const { strip } = this.refs;
    const viewport = strip.parentElement;
    if (!viewport) return;
    const { side, active, gap } = this.#metrics();
    const before = slot * (side + gap); // all cards before `slot` are side-width
    const tx = viewport.clientWidth / 2 - (before + active / 2);
    this.#tx = tx;
    strip.style.transition = animate ? '' : 'none';
    strip.style.transform = `translateX(${tx}px)`;
    if (!animate) {
      requestAnimationFrame(() => {
        strip.style.transition = '';
      });
    }
  }

  /** Re-centre the current active slide (used on resize + drag-cancel). */
  #center(animate) {
    this.#centerSlot(this.#active + 1, animate);
  }

  /** Scale exactly one DOM card (real or clone); clear the rest. */
  #scaleOnly(target) {
    const { card } = this.refs;
    card.forEach((el) => el.classList.toggle('is-active', el === target));
    if (this.#cloneLeft) this.#cloneLeft.classList.toggle('is-active', this.#cloneLeft === target);
    if (this.#cloneRight) this.#cloneRight.classList.toggle('is-active', this.#cloneRight === target);
  }

  /** Show the text block for a real slide index. */
  #showText(index) {
    this.refs.text.forEach((el, i) => el.classList.toggle('is-active', i === index));
  }

  /**
   * Activate a real slide (scale its card, show its text, centre it).
   * @param {number} index
   * @param {boolean} animate
   */
  #activate(index, animate) {
    this.#clearSettle();
    this.#active = index;
    // S83 (slide 23 — "once the 3 cards are done it glitches; should be a continuous loop"):
    // on the silent wrap-snap (animate=false) the strip jumps instantly to the destination slot,
    // whose ANALYTIC centre assumes the active card is already active-width. But the card width
    // has its own 0.3s transition, so the destination card was still side-width (180) at the snap
    // and visibly grew/reflowed for 300ms — the glitch. Freeze the card width transition on the
    // snap so its size change is instant too, matching the instant strip move. Re-enabled next frame.
    if (!animate) this.#setCardTransitions('none');
    this.#scaleOnly(this.refs.card[index]);
    this.#showText(index);
    this.#centerSlot(index + 1, animate);
    if (!animate) {
      void this.refs.strip.offsetWidth; // force reflow so the instant width lands before re-enabling
      requestAnimationFrame(() => this.#setCardTransitions(''));
    }
  }

  /** Toggle the card width/height transition (used to make the wrap-snap size change instant). */
  #setCardTransitions(value) {
    const els = [...this.refs.card];
    if (this.#cloneLeft) els.push(this.#cloneLeft);
    if (this.#cloneRight) els.push(this.#cloneRight);
    els.forEach((el) => {
      el.style.transition = value;
    });
  }

  /** @param {number} delta -1 | +1 */
  #go(delta) {
    const n = this.refs.card.length;
    if (n <= 1) {
      this.#activate(0, true);
      return;
    }
    if (delta > 0 && this.#active === n - 1) return this.#wrap(1);
    if (delta < 0 && this.#active === 0) return this.#wrap(-1);
    this.#activate(this.#active + delta, true);
  }

  /**
   * Continuous wrap. dir +1 = past the end: slide forward into the right clone,
   * then silent-snap to slide 0. dir -1 = past the start: slide into the left
   * clone, then snap to the last slide. The clone is scaled during the animation
   * so the snap is invisible (same image, same size, same centred position).
   * @param {number} dir
   */
  #wrap(dir) {
    const n = this.refs.card.length;
    this.#clearSettle();
    if (this.#reduceMotion) {
      this.#activate(dir > 0 ? 0 : n - 1, false);
      return;
    }
    const clone = dir > 0 ? this.#cloneRight : this.#cloneLeft;
    const cloneSlot = dir > 0 ? n + 1 : 0;
    const landIndex = dir > 0 ? 0 : n - 1;
    this.#showText(landIndex); // text crossfades to the destination during the slide
    this.#scaleOnly(clone); // scale the clone so it matches the active card size
    this.#centerSlot(cloneSlot, true); // animate forward/backward into the clone
    this.#onSettle(() => this.#activate(landIndex, false)); // silent snap to the real slide
  }

  /** Run cb once the strip transform transition ends (with a timeout fallback). */
  #onSettle(cb) {
    const { strip } = this.refs;
    const handler = (e) => {
      if (e && e.target !== strip) return;
      if (e && e.propertyName && e.propertyName !== 'transform') return;
      this.#clearSettle();
      cb();
    };
    this.#settleHandler = handler;
    strip.addEventListener('transitionend', handler);
    this.#settleTimer = window.setTimeout(handler, 360);
  }

  #clearSettle() {
    if (this.#settleHandler) {
      this.refs.strip.removeEventListener('transitionend', this.#settleHandler);
      this.#settleHandler = null;
    }
    if (this.#settleTimer) {
      window.clearTimeout(this.#settleTimer);
      this.#settleTimer = 0;
    }
  }

  // --- drag ---------------------------------------------------------------

  #onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return; // primary button / touch only
    const { strip } = this.refs;
    const viewport = strip.parentElement;
    this.#clearSettle();
    this.#drag = { startX: e.clientX, lastX: e.clientX, startTx: this.#tx, moved: false };
    strip.style.transition = 'none';
    viewport.classList.add('is-dragging');
    try { viewport.setPointerCapture(e.pointerId); } catch {}
  };

  #onPointerMove = (e) => {
    if (!this.#drag) return;
    const dx = e.clientX - this.#drag.startX;
    this.#drag.lastX = e.clientX;
    if (Math.abs(dx) > 3) this.#drag.moved = true;
    e.preventDefault();
    this.refs.strip.style.transform = `translateX(${this.#drag.startTx + dx}px)`;
  };

  #onPointerUp = (e) => {
    const d = this.#drag;
    if (!d) return;
    this.#drag = null;
    const { strip } = this.refs;
    const viewport = strip.parentElement;
    viewport.classList.remove('is-dragging');
    try { viewport.releasePointerCapture(e.pointerId); } catch {}
    const dx = (d.lastX ?? e.clientX) - d.startX;
    const { side } = this.#metrics();
    const threshold = Math.max(36, side / 4);
    if (dx <= -threshold) this.#go(1); // dragged left -> next
    else if (dx >= threshold) this.#go(-1); // dragged right -> prev
    else this.#center(true); // not far enough -> snap back to current
  };
}

if (!customElements.get('oath-how-to-style')) {
  customElements.define('oath-how-to-style', OathHowToStyle);
}
